import { Form, Input, App, Button, Modal, Typography } from 'antd'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { supabase } from '../../utils/supabase'
import { useAuthContext } from '../../context/auth'
import { AuthService } from '../../services/auth/authService'
import { HttpClient } from '../../services/api/client'
import { useFeatureAccessContext } from '../../context/featureAccess'
import {
  FormContainer,
  StyledFormItem,
  FormActions,
  WarningList,
  ConfirmationInput,
  ModalBody,
  ModalWarning,
} from './styles/DeleteAccountFormStyles'

const { Text, Paragraph } = Typography

const CONFIRMATION_PHRASE = 'delete my account'

export function DeleteAccountForm() {
  const { notification } = App.useApp()
  const { user } = useAuthContext()
  const { featureAccess } = useFeatureAccessContext()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')
  const [passwordVerified, setPasswordVerified] = useState(false)

  const hasActiveSubscription = featureAccess?.tier === 'paid'

  const handlePasswordVerification = async (values: { password: string }) => {
    if (!user?.email) {
      notification.error({
        title: 'Error',
        description: 'Unable to verify user email',
      })
      return
    }

    setLoading(true)

    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: values.password,
      })

      if (verifyError) {
        notification.error({
          title: 'Incorrect Password',
          description: 'Please enter your correct password to continue',
        })
        setLoading(false)
        return
      }

      setPasswordVerified(true)
      setShowConfirmModal(true)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'verify_password_for_delete' },
      })
      notification.error({
        title: 'Error',
        description: 'An unexpected error occurred',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (confirmationText.toLowerCase() !== CONFIRMATION_PHRASE) {
      notification.error({
        title: 'Confirmation Required',
        description: `Please type "${CONFIRMATION_PHRASE}" to confirm deletion`,
      })
      return
    }

    setLoading(true)

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token

      const response = await HttpClient.delete('/profile', {
        Authorization: `Bearer ${token}`,
      })

      if (response.error) {
        const isSubscriptionError = response.error.code === 'SUBSCRIPTION_CANCEL_FAILED'
        notification.error({
          title: isSubscriptionError ? 'Subscription Cancellation Failed' : 'Deletion Failed',
          description: isSubscriptionError
            ? 'We were unable to cancel your subscription. Please try again later or contact support@bandos.ai for assistance.'
            : response.error.message || 'Failed to delete account',
          duration: isSubscriptionError ? 8 : 4.5,
        })
        setLoading(false)
        return
      }

      notification.success({
        title: 'Account Deleted',
        description: 'Your account has been permanently deleted',
      })

      // Sign out and redirect to home
      await AuthService.signOut()
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'delete_account' },
      })
      notification.error({
        title: 'Error',
        description: 'An unexpected error occurred while deleting your account',
      })
    } finally {
      setLoading(false)
      setShowConfirmModal(false)
      setConfirmationText('')
      setPasswordVerified(false)
      form.resetFields()
    }
  }

  const handleCancel = () => {
    setShowConfirmModal(false)
    setConfirmationText('')
    setPasswordVerified(false)
    form.resetFields()
  }

  return (
    <FormContainer>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Deleting your account will permanently remove:
      </Paragraph>
      <WarningList>
        <li>All your projects and mindmaps</li>
        <li>Your profile information</li>
        <li>Credit balance and usage history</li>
        <li>All connected integrations</li>
        {hasActiveSubscription && (
          <li>
            <Text type="danger" strong>
              Your active subscription will be canceled immediately
            </Text>
          </li>
        )}
      </WarningList>

      <Form
        form={form}
        layout="vertical"
        onFinish={handlePasswordVerification}
        requiredMark={false}
      >
        <StyledFormItem
          name="password"
          label="Enter your password to continue"
          rules={[{ required: true, message: 'Please enter your password' }]}
        >
          <Input.Password placeholder="Enter your password" size="large" />
        </StyledFormItem>

        <FormActions>
          <Button danger type="primary" htmlType="submit" loading={loading}>
            Delete Account
          </Button>
        </FormActions>
      </Form>

      <Modal
        title="Confirm Account Deletion"
        open={showConfirmModal && passwordVerified}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={loading}
            disabled={confirmationText.toLowerCase() !== CONFIRMATION_PHRASE}
            onClick={handleDeleteAccount}
          >
            Permanently Delete Account
          </Button>,
        ]}
      >
        <ModalBody>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            This action is permanent and cannot be undone. All your data will be removed from our
            servers.
          </Paragraph>

          {hasActiveSubscription && (
            <ModalWarning>
              Your active subscription will be canceled immediately with no refund for the remaining
              period.
            </ModalWarning>
          )}

          <div>
            <Paragraph style={{ marginBottom: 8 }}>
              Type{' '}
              <Text strong code>
                {CONFIRMATION_PHRASE}
              </Text>{' '}
              to confirm:
            </Paragraph>

            <ConfirmationInput
              placeholder={CONFIRMATION_PHRASE}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              status={
                confirmationText && confirmationText.toLowerCase() !== CONFIRMATION_PHRASE
                  ? 'error'
                  : undefined
              }
            />
          </div>
        </ModalBody>
      </Modal>
    </FormContainer>
  )
}
